import { Component, Inject, OnInit } from '@angular/core';
import { Contractor, ContractorAddress, PersonsService } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import Fuse from 'fuse.js';
import { ComplaintParty, ComplaintPartyExtended } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintService } from '../complaint.service';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
    selector: 'icc-complaint-change-user-dialog',
    templateUrl: './complaint-change-user-dialog.component.html',
    styleUrls: ['./complaint-change-user-dialog.component.scss'],
})
export class ComplaintChangeUserDialogComponent implements OnInit {
    recordsOnPage = 25;
    searchWord = '';
    searchNip = '';
    searchAddress = '';
    searchCombined = '';
    contractors: ComplaintPartyExtended[] = [];
    allContractors: ComplaintPartyExtended[] = [];
    selectedContractor: ComplaintPartyExtended | null = null;
    private _page = 1;
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }
    mobileView = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private dialogRef: DialogRef,
        private personsService: PersonsService,
        private complaintService: ComplaintService,
        @Inject(DIALOG_DATA)
        public data: {
            user: ComplaintParty;
            userType: string;
            companyId: number;
            chooseContractorContractor: boolean;
        },
        private breakpointObserver: BreakpointObserver,
    ) {
        this.loadContractors();
    }

    async loadContractors() {
        await this.complaintService.contractorsLoaded;
        this.allContractors = this.complaintService.contractors[this.data.companyId] ?? [];
        this.contractors = this.allContractors;
        this.selectedContractor = this.contractors?.find((c) => c.userId === this.data.user?.userId) ?? null;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.breakpointObserver.observe('(max-width: 550px) or (max-height: 600px)').subscribe((result) => {
                this.mobileView = result.matches;

                if (this.mobileView) {
                    this.searchAddress = '';
                    this.searchNip = '';
                    this.searchWord = '';
                } else {
                    this.searchCombined = '';
                }
            })
        );
    }

    async handleSearch() {
        if (this.searchWord || this.searchNip || this.searchAddress) {
            let contractors = this.allContractors ?? [];

            if (this.searchWord) {
                contractors = this.handleSearchWord(this.searchWord, contractors);
            }
            if (this.searchNip) {
                contractors = this.handleSearchNip(this.searchNip, contractors);
            }
            if (this.searchAddress) {
                contractors = this.handleSearchAddress(this.searchAddress, contractors);
            }

            this.contractors = contractors;
        } else {
            this.contractors = this.allContractors ?? [];
        }
    }

    handleSearchWord(searchWord: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['companyName', 'email', 'phoneNumber'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchWord);
        return result;
    }

    handleSearchNip(searchNip: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['companyVatNr', 'companyVatNrEu'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchNip);
        return result;
    }

    handleSearchAddress(searchAddress: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['address'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchAddress);
        return result;
    }

    getShortName(contractor: ComplaintPartyExtended): string {
        if (contractor?.companyName) {
            return contractor.companyName;
        }
        return '';
    }

    getLocalizationTextFromAddress(address: ContractorAddress): string {
        let localization = '';
        if (address?.countryId) {
            localization = address.countryId;
        }
        if (address?.city) {
            if (localization) localization += ', ';
            localization += address.city;
        }
        if (address?.postalCode) {
            if (localization) localization += ', ';
            localization += address.postalCode;
        }
        return localization;
    }

    getContractorLocalization(contractor: ComplaintPartyExtended): string {
        return contractor.address;
    }

    getContractorEmailPhone(contractor: ComplaintPartyExtended): string {
        let emailPhone = '';
        if (contractor?.email) {
            emailPhone = contractor?.email;
        }
        if (contractor?.phoneNumber) {
            if (emailPhone) emailPhone += ', ';
            emailPhone += contractor.phoneNumber;
        }
        return emailPhone;
    }

    onChangePage(page: number) {
        this.page = page;
    }

    select(contractor: ComplaintPartyExtended) {
        const changed = contractor?.userId !== this.selectedContractor?.userId;
        this.selectedContractor = contractor;
        if (changed) {
            this.dialogRef.close(contractor);
        } else {
            this.dialogRef.close();
        }
    }

    close() {
        this.dialogRef.close();
    }
}
